import { useDispatch, useSelector } from 'react-redux';
import { Dispatch, useEffect, useMemo } from 'react';

import { KnmCollapse } from '../../../KnmPackage/MainModule/components/KnmCollapse';
import { IGroupConditionForm } from '../interfaces/IGroupConditionForm';
import { PassportGroupPassportTypeEnum } from '../enums/PassportGroupPassportTypeEnum';
import { FieldBlock } from '../../../UIPackage/FormModule/components/FieldBlock';
import { SelectInputField } from '../../../UIPackage/MainModule/components/SelectInputField';
import {
  getCatalogKnmStatuses248SelectOptions,
  makeGetCatalogData,
} from '../../../CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { getKnmErpStatusesSelectOptions } from '../../../KnmPackage/MainModule/selectors/knmSelectors';
import { RootActions } from '../../../CorePackage/MainModule/types/RootActions';
import { catalogGet } from '../../../CorePackage/CatalogsModule/actions/catalogsActions';
import { CommonCatalogsNamesEnum } from '../../../CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { KnmErknmStatusCodesEnum } from '../../../APIPackage/CatalogsModule/enums/KnmErknmStatusCodesEnum';
import { KnmErpStatusCodesEnum } from '../../../APIPackage/CatalogsModule/enums/KnmErpStatusCodesEnum';
import { publishedStatusesSelectOptions } from '../../../NewsPackage/MainModule/constants/publishedStatuses';
import { KnmPublishStatusesEnum } from '../../../APIPackage/KnmModule/enums/KnmPublishStatusesEnum';
import { PassportGroupTypeEnum } from '../../../APIPackage/PassportGroupsModule/enums/PassportGroupTypeEnum';
import { createKnmStatusesOption } from '../../../CorePackage/CatalogsModule/common/createKnmStatusesOption';

const ERKNM_STATUS_CODES_FOR_CANCELLATION = [
  KnmErknmStatusCodesEnum.COMMENTS_EXIST,
  KnmErknmStatusCodesEnum.REJECT_APPEALED,
  KnmErknmStatusCodesEnum.TYPE_WAITING_CARRY_OUT,
  KnmErknmStatusCodesEnum.TYPE_CARRIED_OUT,
  KnmErknmStatusCodesEnum.TYPE_COMPLETED,
  KnmErknmStatusCodesEnum.DECISION_APPEALED,
];

const ERKNM_STATUS_CODES_FOR_DELETION = [
  KnmErknmStatusCodesEnum.FILL_IN_PROCESS,
  KnmErknmStatusCodesEnum.READY_FOR_APPROVE,
  KnmErknmStatusCodesEnum.APPROVING,
  KnmErknmStatusCodesEnum.COMMENTS_EXIST,
  KnmErknmStatusCodesEnum.REJECT_APPEALED,
  KnmErknmStatusCodesEnum.TYPE_WAITING_CARRY_OUT,
  KnmErknmStatusCodesEnum.TYPE_CARRIED_OUT,
];

const PM_STATUS_CODES_FOR_CANCELLATION = [
  KnmErknmStatusCodesEnum.TYPE_WAITING_CARRY_OUT,
  KnmErknmStatusCodesEnum.DECISION_APPEALED,
  KnmErknmStatusCodesEnum.REMARK,
];

const PM_STATUS_CODES_FOR_DELETION = [
  KnmErknmStatusCodesEnum.FILL_IN_PROCESS,
  KnmErknmStatusCodesEnum.TYPE_WAITING_CARRY_OUT,
];

const DECISION_STATUS_CODES_FOR_CANCELLATION = [
  KnmErknmStatusCodesEnum.TYPE_COMPLETED,
  KnmErknmStatusCodesEnum.TYPE_APPEALED,
];

const DECISION_STATUS_CODES_FOR_DELETION = [KnmErknmStatusCodesEnum.FILL_IN_PROCESS];

const ERP_STATUS_CODES = [KnmErpStatusCodesEnum.TYPE_NEW, KnmErpStatusCodesEnum.TYPE_WAITING_CARRY_OUT];

interface IPassportStatusGroupConditionProps {
  name: string;
  category: IGroupConditionForm['category'];
  groupType: PassportGroupTypeEnum;
}

export const StatusGroupCondition = ({ name, category, groupType }: IPassportStatusGroupConditionProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const knmStatusesSelectOptionsErknm = useSelector(getCatalogKnmStatuses248SelectOptions);
  const knmStatusesSelectOptionsErp = useSelector(getKnmErpStatusesSelectOptions);
  const knmStatuses294SelectOptions = useSelector(makeGetCatalogData(CommonCatalogsNamesEnum.KNM_STATUSES_294));

  const isErp = category === PassportGroupPassportTypeEnum.ERP;

  useEffect(() => {
    dispatch(catalogGet(CommonCatalogsNamesEnum.KNM_STATUSES_248));
    dispatch(catalogGet(CommonCatalogsNamesEnum.KNM_STATUSES_294));
  }, [dispatch]);

  const erknmStatusCodes = useMemo(() => {
    switch (groupType) {
      case PassportGroupTypeEnum.CANCELLATION:
        return ERKNM_STATUS_CODES_FOR_CANCELLATION;
      case PassportGroupTypeEnum.DELETION:
        return ERKNM_STATUS_CODES_FOR_DELETION;
      default:
        return [];
    }
  }, [groupType]);

  const pmStatusCodes = useMemo(() => {
    switch (groupType) {
      case PassportGroupTypeEnum.CANCELLATION:
        return PM_STATUS_CODES_FOR_CANCELLATION;
      case PassportGroupTypeEnum.DELETION:
        return PM_STATUS_CODES_FOR_DELETION;
      default:
        return [];
    }
  }, [groupType]);

  const decisionStatusCodes = useMemo(() => {
    switch (groupType) {
      case PassportGroupTypeEnum.CANCELLATION:
        return DECISION_STATUS_CODES_FOR_CANCELLATION;
      case PassportGroupTypeEnum.DELETION:
        return DECISION_STATUS_CODES_FOR_DELETION;
      default:
        return [];
    }
  }, [groupType]);

  const statusesSelectOptions = useMemo(() => {
    switch (category) {
      case PassportGroupPassportTypeEnum.ERKNM:
        return knmStatusesSelectOptionsErknm.filter(item =>
          erknmStatusCodes.includes(item.code as KnmErknmStatusCodesEnum),
        );
      case PassportGroupPassportTypeEnum.PM:
        return knmStatusesSelectOptionsErknm.filter(item =>
          pmStatusCodes.includes(item.code as KnmErknmStatusCodesEnum),
        );
      case PassportGroupPassportTypeEnum.DECISION_PASSPORT:
        return knmStatuses294SelectOptions
          .filter(item => decisionStatusCodes.includes(item.code as KnmErknmStatusCodesEnum))
          .map(createKnmStatusesOption);
      case PassportGroupPassportTypeEnum.ERP:
        return knmStatusesSelectOptionsErp.filter(item =>
          ERP_STATUS_CODES.includes(item.code as KnmErpStatusCodesEnum),
        );
      default:
        return [];
    }
  }, [
    knmStatusesSelectOptionsErknm,
    knmStatusesSelectOptionsErp,
    knmStatuses294SelectOptions,
    category,
    erknmStatusCodes,
    pmStatusCodes,
    decisionStatusCodes,
  ]);

  const publishedStatusSelectOptions = useMemo(() => {
    if (isErp) {
      return publishedStatusesSelectOptions;
    }

    return publishedStatusesSelectOptions.filter(item =>
      [KnmPublishStatusesEnum.PUBLISHED, KnmPublishStatusesEnum.UNPUBLISHED].includes(item.value),
    );
  }, [isErp]);

  return (
    <KnmCollapse title="Статусы мероприятия">
      <>
        <FieldBlock label={isErp ? 'Статус проверки' : 'Статус мероприятия'} margin="bottom">
          <SelectInputField name={`${name}.statuses`} options={statusesSelectOptions} isClearable isMulti />
        </FieldBlock>
        <FieldBlock label={isErp ? 'Статус публикации проверки' : 'Статус публикации мероприятия'} margin="bottom">
          <SelectInputField name={`${name}.publishedStatus`} options={publishedStatusSelectOptions} />
        </FieldBlock>
      </>
    </KnmCollapse>
  );
};
